import { useAccount } from "@azure/msal-react";
import { motion } from "framer-motion";
import { FC, useContext, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { BiPlusCircle } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import Autocomplete from "../components/ui/Autocomplete";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import Heading from "../components/ui/Heading";
import Input from "../components/ui/Input";
import Select from "../components/ui/Select";
import Textarea from "../components/ui/Textarea";
import { AppContext } from "../context/AppContext";
import { GroupsContext } from "../context/GroupsContext";
import handleToastError from "../helpers/handleToastError";
import useApi from "../hooks/useApi";
import { CreateGroupRequest, GroupType } from "../types/ApiTypes";

interface IGroupType {
  value: GroupType;
  label: string;
}

interface InputFields {
  name: string;
  beschreibung: string;
}

const groupTypeOptions: IGroupType[] = [
  { value: 50, label: "Workspace" },
  { value: 55, label: "Projekt" },
  { value: 56, label: "Service Project" }
];

const referenceType = [
  // { value: 'order', label: 'Auftrag'},
  { value: 'project', label: 'Projekt' },
]

const CreateCustomers: FC = () => {
  const navigate = useNavigate();
  const appCtx = useContext(AppContext);
  const groupCtx = useContext(GroupsContext);
  const account = useAccount();
  const { register, formState: { errors }, watch, handleSubmit, setValue } = useForm<InputFields>();
  const { fetchCompanies, createGroupRequest, fetchOrdersForCustomer } = useApi();

  const [employees, setEmployees] = useState<{ label: string, value: string }[]>([]);
  const [customers, setCustomers] = useState<{ label: string, value: { id: string, ownerId: string } }[]>([]);
  const [orders, setOrders] = useState<{ label: string, value: string }[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [projects, setProjects] = useState<{ label: string, value: { id: string, teamLead: string, manager: string } }[]>([]);

  const [selectedGroupType, setSelectedGroupType] = useState<IGroupType>(groupTypeOptions[0]);
  const [selectedEmployee, setSelectedEmployee] = useState<{ label: string, value: string }>();
  const [selectedOrder, setSelectedOrder] = useState<{ label: string, value: string }>();
  const [selectedProject, setSelectedProject] = useState<{ label: string, value: { id: string, teamLead: string, manager: string } }>();
  const [selectedCustomer, setSelectedCustomer] = useState<{ label: string, value: { id: string, ownerId: string } }>();
  const [selectedManager, setSelectedManager] = useState<{ label: string, value: string }>();
  const [selectedReferenceType, setSelectedReferenceType] = useState<{ label: string, value: string }>();
  // const [customErrors, setCustomErrors] = useState<{employee:string, customer:string, order:string}>({
  //     employee: '',
  //     customer: '',
  //     order:''
  // });

  useEffect(() => {
    if (appCtx.state.user) {
      setEmployees(appCtx.state.user.map(x => ({ label: x.displayName, value: x.id })));
    }
  }, [appCtx.state.user]);

  useEffect(() => {
    if (selectedGroupType.value !== 50) setValue("name", '');
    if (selectedCustomer && selectedGroupType.value === 55 && selectedReferenceType) {
      setOrdersLoading(true);
      setOrders([]);
      setProjects([]);
      if (selectedReferenceType.value === 'order') {
        fetchOrdersForCustomer(selectedCustomer.value.id).then(data => {
          if (!data) return setOrders([]);
          setOrders(data.map(x => ({ label: `${x.orderNumber} - ${x.name}`, value: x.orderNumber })));
        }).finally(() => setOrdersLoading(false));
      } else {
        setProjects(appCtx.state.projects.filter(x => x._pvt_customer_value === selectedCustomer.value.id && x.pvt_projectkind !== 921920001 && !x.pvt_internalproject).map(x => ({
          label: `${x.pvt_projectnumber} - ${x.pvt_projectname}`,
          value: { id: x.pvt_projectnumber, teamLead: '', manager: x._pvt_projectowner_value }
        })));
      }
    }
    if (selectedCustomer && selectedGroupType.value === 56) {
      setProjects([]);
      setProjects(appCtx.state.projects.filter(x => x._pvt_customer_value === selectedCustomer.value.id && x.pvt_projectkind === 921920001 && !x.pvt_internalproject).map(x => ({
        label: `${x.pvt_projectnumber} - ${x.pvt_projectname}`,
        value: { id: x.pvt_projectnumber, teamLead: '', manager: x._pvt_projectowner_value }
      })));
    }
  }, [selectedCustomer, selectedGroupType, selectedReferenceType, fetchOrdersForCustomer, appCtx.state.projects]);

  useEffect(() => {
    setSelectedEmployee(undefined);
    setSelectedManager(undefined);
  }, [selectedReferenceType])

  useEffect(() => {
    setCustomersLoading(true);
    fetchCompanies().then(data => {
      if (data) setCustomers(data.sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(x => ({
        label: `${x.name}${x.pvt_customernumber ? ` - ${x.pvt_customernumber}` : ''}`,
        value: { id: x.accountid, ownerId: x.ownerid }
      })));
    }).finally(() => setCustomersLoading(false));
  }, [fetchCompanies]);


  const onGroupTypeChange = (type: IGroupType) => {
    setSelectedGroupType(type);
  };

  const onEmployeeSelected = (type: { label: string, value: string }) => {
    setSelectedEmployee(type);
  };

  const onManagerSelected = (type: { label: string, value: string }) => {
    setSelectedManager(type);
  };

  const onOrderChange = (type: { label: string, value: string }) => {
    setSelectedOrder(type);
  };

  const onReferenceTypeChange = (type: { label: string, value: string }) => {
    setSelectedReferenceType(type);
  }

  const onProjectChange = (type: { label: string, value: { id: string, teamLead: string, manager: string } }) => {
    setSelectedProject(type);
    const teamLead = appCtx.state.user.find(x => x.dynamicsId === type.value.teamLead);
    if (teamLead) {
      setSelectedEmployee({
        label: teamLead.displayName,
        value: teamLead.id
      });
    };
    const manager = appCtx.state.user.find(x => x.dynamicsId === type.value.manager);
    if (manager) {
      setSelectedManager({
        label: manager.displayName,
        value: manager.id
      });
    }
  };

  const onCustomerSelected = (type: { label: string, value: { id: string, ownerId: string } }) => {
    if (selectedGroupType.value === 50) {
      const customer = customers.find(x => x.value.id === type.value.id);
      // If label ends with a 5 digit number, remove it for the name
      if (customer?.label) {
        const regex = /^(.*?)(\s-\s\d{5})?$/;
        const match = customer.label.match(regex);
        if (match) {
          setValue("name", match[1]);
        } else {
          setValue("name", customer.label);
        }
      }
    }
    setSelectedCustomer(type);
  };

  const onSubmit: SubmitHandler<InputFields> = async (e) => {
    let errors = 0;
    if (!selectedEmployee) {
      console.log('Kein Stellvertreter wurde ausgewählt')
      //setCustomErrors((prev) => ({...prev,employee: 'Kein Bereich wurde ausgewählt'}))
      errors++;
    }
    if (!selectedCustomer) {
      console.log('Kein Kunde wurde ausgewählt')
      //setCustomErrors((prev) => ({...prev,customer: 'Kein Kunde wurde ausgewählt'}))
      errors++;
    }
    if (!selectedOrder && !selectedProject && selectedGroupType.value === 55) {
      console.log('Kein Auftrag wurde ausgewählt')
      //setCustomErrors((prev) => ({...prev,order: 'Kein Auftrag wurde ausgewählt'}))
      errors++;
    }
    if (!selectedManager && selectedGroupType.value === 56) {
      console.log('Kein Projektowner wurde ausgewählt')
      //setCustomErrors((prev) => ({...prev,order: 'Kein Projektowner gefunden'}));
      errors++;
    }
    if (!selectedManager && selectedGroupType.value === 55 && selectedReferenceType?.value === 'project') {
      console.log('Kein Projektowner wurde ausgewählt')
      //setCustomErrors((prev) => ({...prev,order: 'Kein Projektowner gefunden'}));
      errors++;
    }
    if (errors) return;
    let data: CreateGroupRequest = {
      type: selectedGroupType.value,
      name: e.name,
      customer: selectedCustomer!.value.id,
      description: e.beschreibung,
      primary_owner: selectedManager!.value,
      secondary_owner: selectedEmployee!.value,
      requester: account!.localAccountId
    };

    if (selectedGroupType.value === 56 && selectedProject) data.project = selectedProject.value.id;
    if (selectedGroupType.value === 55 && selectedOrder) data.project = selectedOrder.value;

    await toast.promise(createGroupRequest(data), {
      loading: "Der Formular wird abgeschickt",
      success: (res) => {
        groupCtx.dispatch({ type: "REPLACE_GROUP", payload: res });
        //setCustomErrors({employee: '',customer: '',order: ''});
        navigate(`/space/${res.requestId}`);
        return <span
          className="text-sm">Der Antrag für den Bereich<br /><b>{selectedGroupType.value} {watch("name")}</b> wurde erstellt.</span>;
      },
      error: (err) => handleToastError(err, `Der Antrag für den Bereich ${selectedGroupType.value} ${watch("name")} konnte nicht erstellt werden.`)
    });
  };

  return (
    <motion.section initial={{
      opacity: 0
    }} exit={{
      opacity: 0
    }} animate={{
      opacity: 1
    }}>
      <Heading>Erstelle einen Kundenbereich</Heading>
      <div className="gap-4 grid grid-cols-1 md:grid-cols-2">
        <div className="flex flex-col space-y-4">
          <Card>
            <form autoComplete="off" className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label className="font-semibold">Spacetyp</label>
                <Select options={groupTypeOptions} defaultValue={groupTypeOptions[0]} onChange={onGroupTypeChange} />
              </div>
              <div>
                <label className="font-semibold" htmlFor="Kunde">Kunde</label>
                <Autocomplete<{ id: string, ownerId: string }> loading={customersLoading} options={customers}
                  onChange={onCustomerSelected} />
              </div>
              {selectedGroupType.value === 55 && selectedCustomer && (
                <div>
                  <label className="font-semibold" htmlFor="referenztyp">Referenztyp</label>
                  <Select options={referenceType} onChange={onReferenceTypeChange} />
                </div>
              )}
              {selectedGroupType.value === 55 && selectedCustomer && selectedReferenceType?.value === 'order' && (
                <div>
                  <label className="font-semibold" htmlFor="auftrag">Auftrag</label>
                  <Select loading={ordersLoading} options={orders} onChange={onOrderChange} />
                </div>
              )}
              {((selectedGroupType.value === 56 && selectedCustomer) || selectedReferenceType?.value === 'project') && (
                <div>
                  <label className="font-semibold" htmlFor="projekt">Projekt</label>
                  <Select options={projects} onChange={onProjectChange} />
                </div>
              )}
              {selectedGroupType.value !== 50 && (
                <div>
                  <label className="font-semibold" htmlFor="name">Name</label>
                  <Input label="Name" id="name" className="block" placeholder="Name" {...register("name", {
                    required: selectedGroupType.value !== 55,
                    minLength: 5
                  })} />
                  {errors.beschreibung &&
                    <span className="text-error-brighter text-xs">Name ist ein Pflichtfeld und muss mindestens 5 Zeichen lang sein.</span>}
                </div>
              )}
              <div>
                <label className="font-semibold" htmlFor="beschreibung">Beschreibung</label>
                <Textarea label="Beschreibung" id="beschreibung" className="block"
                  placeholder="Beschreibung" {...register("beschreibung", { required: true, minLength: 30 })} />
                {errors.beschreibung &&
                  <span className="text-error-brighter text-xs">Beschreibung ist ein Pflichtfeld und muss mindestens 30 Zeichen lang sein.</span>}
              </div>
              {(selectedGroupType.value === 50 || selectedGroupType.value === 55) && (
                <div>
                  <label className="font-semibold" htmlFor="Besitzer">Besitzer</label>
                  <Autocomplete<string> defaultValue={selectedManager} options={employees} onChange={onManagerSelected} />
                </div>
              )}
              <div>
                <label className="font-semibold" htmlFor="Stellvertreter">Stellvertreter</label>
                <Autocomplete<string> defaultValue={selectedEmployee} options={employees} onChange={onEmployeeSelected} />
              </div>
              <Button type="submit"><BiPlusCircle className="mr-1" /> Erstellen</Button>
            </form>
          </Card>
        </div>
        <div className="flex flex-col space-y-4">
          <Card title={`${selectedGroupType.value} ${watch("name") ? watch("name") : "Name des Arbeitsbereiches"}`}>
            <div className="space-y-4">
              <span className="italic">{watch("beschreibung")}</span>
              <ul>
                {selectedCustomer && <li>Kunde: {selectedCustomer.label}</li>}
                {selectedCustomer && <li>Besitzer: {selectedManager?.label}</li>}
                {selectedOrder && <li>Auftrag: {selectedOrder.label}</li>}
                {selectedEmployee && <li>Stellvertreter: {selectedEmployee.label}</li>}
              </ul>
            </div>
          </Card>
        </div>
      </div>
    </motion.section>
  );
};

export default CreateCustomers;